import { useEffect, useRef } from 'react';

/**
 * Baseline modal accessibility shared by every dialog/drawer in the app:
 *  - Escape closes it
 *  - Tab is trapped inside the dialog while it's open
 *  - focus moves into the dialog on open, and back to whatever triggered it on close
 * Attach the returned ref to the modal's outermost container element.
 */
export function useModalA11y(isOpen, onClose) {
    const containerRef = useRef(null);
    const previouslyFocused = useRef(null);

    useEffect(() => {
        if (!isOpen) return;

        previouslyFocused.current = document.activeElement;

        const getFocusable = () => containerRef.current
            ? Array.from(containerRef.current.querySelectorAll(
                'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
            )).filter(el => el.offsetParent !== null)
            : [];

        // Move focus into the dialog once it mounts — but only if nothing inside it has
        // already claimed focus (e.g. a field with `autoFocus`, like CreatePollModal's
        // question textarea). Stealing focus away from that would undo the point of autoFocus.
        const raf = requestAnimationFrame(() => {
            if (containerRef.current?.contains(document.activeElement)) return;
            const items = getFocusable();
            if (items.length) items[0].focus();
            else containerRef.current?.focus();
        });

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose?.();
                return;
            }
            if (e.key === 'Tab') {
                const items = getFocusable();
                if (items.length === 0) return;
                const first = items[0];
                const last = items[items.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown, true);
        return () => {
            cancelAnimationFrame(raf);
            document.removeEventListener('keydown', handleKeyDown, true);
            if (previouslyFocused.current && typeof previouslyFocused.current.focus === 'function') {
                previouslyFocused.current.focus();
            }
        };
    }, [isOpen, onClose]);

    return containerRef;
}
